/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import useDebounce from "../hooks/useDebounce";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./SystemLogPage.css";

/* Ant Design */
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Alert,
  Row,
  Col,
  message,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  LockOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const SystemLogPage = () => {
  const { authData } = useAuth();

  /* --------- access scope --------- */
  const roleStr = String(authData?.role || "").toLowerCase();
  const canViewAllHotels =
    roleStr === "systemadmin" ||
    (Array.isArray(authData?.permissions) &&
      authData.permissions.includes("Logs.ViewAll"));

  /* ---------------- state ---------------- */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // filters
  const [eventType, setEventType] = useState("");
  const [userEmail, setUserEmail] = useState(""); // ✅ email search (not userId)

  // Hotel filter only if allowed
  const [hotelId, setHotelId] = useState(
    canViewAllHotels ? "" : authData?.hotelId ?? ""
  );

  // debounce
  const debouncedType = useDebounce(eventType, 300);
  const debouncedEmail = useDebounce(userEmail, 300);
  const debouncedHotelId = useDebounce(hotelId, 300);

  /* keep hotelId in sync if auth changes and user cannot view all */
  useEffect(() => {
    if (!canViewAllHotels) {
      setHotelId(authData?.hotelId ?? "");
    }
  }, [authData?.hotelId, canViewAllHotels]);

  /* ---------------- effects ---------------- */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);

    // require a hotelId when user is restricted
    if (!canViewAllHotels && !authData?.hotelId) {
      setLoading(false);
      setErrorMsg("Missing hotel scope. Please re-login or contact admin.");
      return;
    }

    try {
      const params = {
        // If your API expects `userEmail` instead of `email`, rename this key.
        email: debouncedEmail || undefined,
        eventType: debouncedType || undefined,
      };

      if (canViewAllHotels) {
        if (debouncedHotelId) params.hotelId = debouncedHotelId;
      } else {
        params.hotelId = authData?.hotelId;
      }

      const res = await axiosInstance.get("/auth/system-logs", { params });
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Log fetch failed", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load logs. Please try again.";
      setErrorMsg(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedType,
    debouncedEmail,
    debouncedHotelId,
    canViewAllHotels,
    authData?.hotelId,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /* ---------------- client-side email filter (fallback / contains) ---------------- */
  const displayedLogs = useMemo(() => {
    if (!userEmail) return logs;
    const q = userEmail.toLowerCase();
    return logs.filter((l) => String(l.email || "").toLowerCase().includes(q));
  }, [logs, userEmail]);

  /* ---------------- export ---------------- */
  const exportCsv = () => {
    if (!displayedLogs.length) {
      message.info("No logs to export.");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(displayedLogs);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Logs");
    const buf = XLSX.write(wb, { type: "array", bookType: "csv" });
    saveAs(
      new Blob([buf], { type: "text/csv;charset=utf-8;" }),
      `system_logs_${Date.now()}.csv`
    );
    message.success("Exported CSV");
  };

  /* ---------------- helpers ---------------- */
  const typeColor = (t = "") => {
    const key = t.toLowerCase();
    if (key.includes("login") || key.includes("created")) return "green";
    if (key.includes("logout")) return "geekblue";
    if (key.includes("fail") || key.includes("error")) return "red";
    if (key.includes("update") || key.includes("edit")) return "gold";
    if (key.includes("delete") || key.includes("remove")) return "volcano";
    return "default";
  };

  const uniqueEventTypes = useMemo(
    () =>
      Array.from(new Set(logs.map((l) => l?.eventType).filter(Boolean))).sort(),
    [logs]
  );

  /* ---------------- table ---------------- */
  const columns = [
    {
      title: "Time",
      dataIndex: "eventTime",
      key: "eventTime",
      width: 200,
      sorter: (a, b) => new Date(a.eventTime) - new Date(b.eventTime),
      defaultSortOrder: "descend",
      render: (_, r) => {
        const iso = new Date(r.eventTime).toISOString();
        const local = new Date(r.eventTime).toLocaleString();
        return (
          <Tooltip title={`UTC: ${iso}`}>
            <Text>{local}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Hotel",
      dataIndex: "hotelId",
      key: "hotelId",
      width: 90,
      align: "right",
      render: (v) => (v ?? "-"),
      sorter: (a, b) => (a.hotelId ?? 0) - (b.hotelId ?? 0),
    },
    {
      title: "User",
      dataIndex: "userId",
      key: "userId",
      width: 90,
      align: "right",
      render: (v) => (v ?? "-"),
      sorter: (a, b) => (a.userId ?? 0) - (b.userId ?? 0),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 320,                          // a bit wider than before
      onCell: () => ({ className: "log-email-cell" }), // let it wrap
      render: (email) =>
        email ? (
          <Space size={6} wrap>
            <a className="log-email-link" href={`mailto:${email}`}>{email}</a>
            <Text copyable={{ text: email }} />
          </Space>
        ) : (
          "-"
        ),
      sorter: (a, b) => String(a.email || "").localeCompare(String(b.email || "")),
    }
    ,
    {
      title: "Event",
      dataIndex: "eventType",
      key: "eventType",
      width: 180,
      ellipsis: true,
      filters: uniqueEventTypes.map((et) => ({ text: et, value: et })),
      onFilter: (value, record) => (record.eventType || "") === value,
      render: (t) => (t ? <Tag color={typeColor(t)}>{t}</Tag> : "-"),
      sorter: (a, b) =>
        String(a.eventType || "").localeCompare(String(b.eventType || "")),
    },
    {
      title: "IP",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 140,
      render: (ip) =>
        ip ? (
          <Text code copyable>
            {ip}
          </Text>
        ) : (
          "-"
        ),
      sorter: (a, b) =>
        String(a.ipAddress || "").localeCompare(String(b.ipAddress || "")),
    },
    {
      title: "User Agent",
      dataIndex: "userAgent",
      key: "userAgent",
      ellipsis: true,
      render: (ua) =>
        ua ? (
          <Tooltip title={ua}>
            <Text>{ua}</Text>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  /* ---------------- render ---------------- */
  return (
    <div className="systemlog-container">
      <Breadcrumb />

      <Card
        className="systemlog-card"
        title={
          <Space wrap>
            <InfoCircleOutlined />
            <Text strong>System Logs</Text>
            {!canViewAllHotels && (
              <Tag icon={<LockOutlined />} color="default">
                Scope: Your Hotel Only
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchLogs} loading={loading}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportCsv}>
              Export CSV
            </Button>
          </Space>
        }
      >
        {/* filters */}
        <Form layout="vertical">
          <Row gutter={[12, 12]}>
            {canViewAllHotels && (
              <Col xs={24} md={8}>
                <Form.Item label="Hotel ID (Admin)">
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="Leave blank for all"
                    value={hotelId}
                    onChange={(e) => setHotelId(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} md={canViewAllHotels ? 8 : 12}>
              <Form.Item label="Event Type">
                <Input
                  placeholder="e.g., LoginSucceeded"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                  list="event-types"
                />
                <datalist id="event-types">
                  {uniqueEventTypes.map((et) => (
                    <option key={et} value={et} />
                  ))}
                </datalist>
              </Form.Item>
            </Col>

            <Col xs={24} md={canViewAllHotels ? 8 : 12}>
              <Form.Item label="Email">
                <Input
                  type="text"
                  inputMode="email"
                  placeholder="user@example.com (supports partial match)"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {errorMsg && (
          <Alert
            type="error"
            message="Error loading logs"
            description={errorMsg}
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <Table
          rowKey="systemLogId"
          columns={columns}
          dataSource={displayedLogs}
          loading={loading}
          size="middle"
          bordered
          sticky
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
          }}
          scroll={{ x: 1000, y: 520 }}
          locale={{ emptyText: "No logs found." }}
        />
      </Card>
    </div>
  );
};

export default SystemLogPage;
